/* eslint-disable max-lines */
import { IModel, IModelResponse } from '@cian/http-api/shared/model';
import { ECurrency, EPaymentPeriod, EPriceType } from '../../../monolith-python/entities/offer/BargainTermsSchema';
import { ITermsDuration, EMoneySource } from '../../../../redux/reducers';

export type TPublishTermsModel = IModel<IPublishTermsRequest, TPublishTermsResponse>;

export interface IPublishTermsRequest {
  /** Категория объявления */
  category?: string;
  /** ID объявления */
  id?: number;
  /** Кол-во фотографий у объявления */
  photosCount?: number;
  /** Долгота */
  longitude?: number;
  /** Широта */
  latitude?: number;
  /** Мультиразмещение */
  isMultiListing?: boolean;
  /** Список ID гео-привязок */
  locationPath?: Array<number>;
  /** ID ТЦ/БЦ или корпуса */
  businessShoppingCenterId?: number;
  /** ID ЖК */
  newObjectId?: number;
  /** ID корпуса ЖК */
  newObjectHouseId?: number;
  /** Объявление восстановлено из архива */
  isFromArchive?: boolean;
  /** Валюта **/
  currency?: ECurrency | null;
  /** Тип цены **/
  priceType?: EPriceType | null;
  /** Период оплаты **/
  paymentPeriod?: EPaymentPeriod | null;
  /** Цена **/
  price?: number | null;
  /** Общая площадь, м2 **/
  totalArea?: number | null;
  /**
   * Для эксперимента MONEY-382 флаг для бэка о возможности отрисовки нового виджета
   * для веб формы может быть только desktop
   * после окончания эксперимента будет удален
   **/
  uiType: 'desktop';
  /** Счёт размещения **/
  moneySource: EMoneySource | null;
  /** Параметры объявления для динамического ценообразования */
  extraData: object;
  /** Количество слотов в мультиобъявлении */
  multiListingSlotsCount?: number | null;
  /** Флаг Собственник или Агент, выбранный на ФП в самом начале */
  isByHomeowner?: boolean;
  /** Выбран ли тип размещения "Из пакета" */
  isPackageOnUi?: boolean | null;
}

export type TPublishTermsResponse = IPublishTermsResponse200 | IPublishTermsResponse400;

export interface IPublishTermsResponse200 extends IModelResponse<IPublishTermsResponse> {
  statusCode: 200;
}

export interface IPublishTermsResponse400 extends IModelResponse<IPublishTermsResponseError> {
  statusCode: 400;
}

export interface IPublishTermsResponse {
  /** Автопролонгация */
  autoProlong: boolean;
  /** Высокий спрос */
  isHighDemand: boolean;
  /** Публикация на других площадках **/
  isMultipostingAvailable: boolean;
  /** Параметры сообщений при публикации */
  message?: IMessageSchema | null;
  /** Услуги размещения */
  terms?: Array<ITermSchema> | null;
  /** Пакеты размещения */
  servicePackages?: Array<IServicePackageSchema> | null;
  /** Данные аукциона **/
  auction?: IAuctionSchema | null;
  /** Данные коллтрекинга **/
  calltracking?: ICalltrackingSchema | null;
  /** Данные публикации **/
  publication?: IPublicationSchema | null;
  /** Публикация на других площадках **/
  multiposting?: IMultipostingSchema | null;
  /** Рекомендованные пакеты **/
  recommendedServicePackages?: IRecommendedPackageSchema[];
  /** Признак эксперимента MONEY-382 **/
  withoutMinimalBet?: boolean;
  /** Счёт размещения **/
  moneySource: EMoneySource;
  /** Коэффициент дифф. цен */
  highDemandCoefficient: number | null;
  /** Выбран ли тип размещения "Из пакета" */
  isAllowPackage: boolean;
}

interface IMessageSchema {
  /** Параметры модалки "Требуемый тариф" */
  needTariff?: INeedTariffTermSchema;
  /** Параметры модалки "Не установлен тариф" */
  noTariff?: INoTariffTermSchema;
  /** Параметры модалки "Субагент исчерпал лимит" */
  subAgentLimitExceed?: ISubAgentLimitExceedTermSchema;
}

interface INeedTariffTermSchema {
  /** Название категории */
  categoryTypeName: string;
  /** Название локации */
  locationName: string;
  /** Название минимального тарифа */
  minimalTariffTitleName: string;
  /** Необходимая сумма для активации тарифа */
  neededActivatedAmount: number;
  /** Ссылка на выбор тарифа */
  priceUrl: string;
}

interface INoTariffTermSchema {
  /** ID локации */
  locationId: number;
  /** Название локации */
  locationName: string;
  /** Необходимая сумма для активации тарифа */
  minActivateAmount: number;
  /** ID пакета */
  packageId: number;
  /** Название пакета */
  packageName: string;
}

interface ISubAgentLimitExceedTermSchema {
  /** Тело сообщения */
  body: string;
  /** Заголовок */
  title: string;
}

export interface ITermSchema {
  /** Необходима идентификация пользователя */
  identificationRequired: boolean;
  /** Причина недоступности тарифа */
  unavailableReasonType: EUnavailableReason | null;
  /** Является дополнительным */
  isAdditional: boolean;
  /** Список несовместимых сервисов для текущего */
  incompatibleServices: Array<EServiceType>;
  /** Является тарифом по-умолчанию */
  isDefault: boolean;
  /** Доступна ли автопролонгация у тарифа */
  isAutoProlongAvailable: boolean;
  /** Количество доступных действий, сколько типов размещения можно купить */
  availableActionsCount: number;
  /** ID текущего сервиса */
  services: Array<EServiceType>;
  /** Доступен ли тариф в данном случае */
  isAvailable: boolean;
  /** Уникальный ключ тарифа */
  key: string;
  /** Базовая ставка услуги */
  baseAuctionBet: number | null;
  /** Сроки размещения */
  periods?: Array<IServicePeriodSchema> | null;
}

export interface IAuctionSchema {
  /** Параметры автоброкера */
  strategyInfo?: IAuctionStrategyInfoSchema | null;
  /** Параметры аукциона */
  auctionInfo?: IAuctionInfoSchema | null;
  /** Список несовместимых сервисов для аукциона */
  incompatibleServices?: Array<EServiceType> | null;
  /** Доступен ли аукцион */
  isAvailable: boolean;
  /** Срок размещения */
  period?: IServicePeriodSchema | null;
  /** Сроки размещения */
  durations?: ITermsDuration[];
  /** Признак выключенного продвижения **/
  withoutPromotion?: boolean;
}

export interface IAuctionStrategyInfoSchema {
  /** Включен */
  isEnabled?: boolean | null;
  /** Список услуг */
  serviceTypes?: Array<EServiceType> | null;
  /** Название */
  strategyName?: string | null;
}

export interface IServicePeriodSchema {
  /** Размер скидки, % */
  discount: number;
  /** Дата, до которой оплачено объявление */
  paidTill: string | null;
  /** Цена без скидки */
  marketingPrice: string;
  /** Цена со скидкой (если она есть) */
  price: string;
  /** Сколько осталось оплаченных дней для типов размещения на период */
  paidDaysLefts: number;
  /** Дата окончания публикации */
  endDate: string;
  /** Указывает на то, что объявление будет тарифицироваться в рамках купленного пакета по объему */
  isShouldPaidByServicePackageSlot: boolean;
  /** Остаток свободных слотов в купленном пакете по объему */
  servicePackageActualCount: number;
  /** Название периода */
  label: string;
  /** Оплачен */
  isPaid: boolean;
  /** На сколько дней рассчитан период */
  days: number;
  /** Список услуг */
  incompatibleServices: Array<EServiceType>;
  /** Является периодом по-умолчанию */
  isDefault: boolean;
  /** Дата окончания действия пакета = дата до которого будет опубликовано объявление за счет пакета */
  servicePackageEndDate: string;
  /** Объект тарифа */
  tariffIdentificator: ITariffIdentificatorSchema;
  /** Тип тарификации */
  type: EPublicationType;
  /** Дата окончания активности */
  activeTill: string | null;
  /** Код периода */
  key: string;
  /** Токен срока размещения. Нужен только для отправки на бэк */
  token: string | null;
}

export interface IServicePackageSchema {
  /** Размер скидки, % */
  discount: number;
  /** Дата, до которой оплачено объявление */
  paidTill: string | null;
  /** Цена без скидки */
  marketingPrice: string;
  /** Цена со скидкой (если она есть) */
  price: string;
  /** Сколько осталось оплаченных дней для типов размещения на период */
  paidDaysLefts: number;
  /** Дата окончания публикации */
  endDate: string;
  /** Указывает на то, что объявление будет тарифицироваться в рамках купленного пакета по объему */
  isShouldPaidByServicePackageSlot: boolean;
  /** Остаток свободных слотов в купленном пакете по объему */
  servicePackageActualCount: number;
  /** Название периода */
  label: string;
  /** Оплачен */
  isPaid: boolean;
  /** На сколько дней рассчитан период */
  days: number;
  /** Список услуг */
  incompatibleServices: Array<EServiceType>;
  /** Является периодом по-умолчанию */
  isDefault: boolean;
  /** Дата окончания действия пакета = дата до которого будет опубликовано объявление за счет пакета */
  servicePackageEndDate: string;
  /** Объект тарифа */
  tariffIdentificator: ITariffIdentificatorSchema;
  /** Тип тарификации */
  type: EPublicationType;
  /** Дата окончания активности */
  activeTill: string | null;
  /** Код периода */
  key: string;
  /** Токен пакета. Нужен только для отправки на бэк */
  token: string | null;
}

export interface ITariffIdentificatorSchema {
  /** Количество дней публикации */
  days: number;
  /** Id используемого пакета (тарифа в регионе) пользователя */
  packageId: number;
  /** Тип периода публикации */
  periodType: string;
  /** Список услуг */
  serviceTypes: string[];
  /** Тип тарифной сетки */
  tariffGridType: string;
  /** Id тарифной сетки */
  tariffId: number;
  diffPriceId: number;
  diffPriceIterationId: string;
  diffPriceGranuleGuid: string;
}

export interface IAuctionInfoSchema {
  configurations?: Array<IConfigurationInfoSchema> | null;
  /** Текущая ставка */
  currentBet?: number | null;
  /** Ставка по умолчанию в поле карточки стандарт (должна совпадать с currentBet, если не выбрана карточка VAS) */
  defaultBet?: number | null;
  /** Доступно редактирование */
  isEditable?: boolean | null;
  /** Максимальная ставка */
  maximalBet?: number | null;
  /** Минимальная ставка */
  minimalBet?: number | null;
  /** Список минимальных ставок для перехода на следующую услугу */
  serviceCosts?: Array<IAuctionServiceCostSchema> | null;
  /** ставки для кнопки "+" поля ввода ставки */
  nextBets?: number[];
  /** ставки для кнопки "-" поля ввода ставки */
  previousBets?: number[];
}

export interface IConfigurationInfoSchema {
  /** Список услуг */
  serviceTypes: string[];
  /** Шаг изменения ставки */
  stepBet: number;
}

export interface IAuctionServiceCostSchema {
  /** Цена */
  cost: string;
  /** Список услуг */
  serviceTypes: Array<EServiceType>;
}

export interface IRecommendedPackageSchema {
  /** Id рекомендованного смарт-пакета */
  id: number;
  /** Текст заголовка блока смарт-пакета */
  headerText: string;
  /** текст хинта блока смарт-пакета */
  tooltipText: string;
  /** Длительность пакета в днях */
  durationInDays: number;
  /** Названия пегионов действия смарт-пакета */
  regionNames: string[];
  /** Название категории в карточке смарт-пакета */
  categoriesView: string;
  /** Цена смарт-пакета */
  price: number;
  /** Скидка на смарт-пакет */
  discountPercent: number;
  /** Цена без скидки */
  marketingPrice: number;
  /** Цена с учетом скидки */
  announcementPrice: number;
  /** Дата начала действия смарт-пакета */
  packageStartDate: string;
  /** Дата окончания действия смарт-пакета */
  packageEndDate: string;
  /** Общее количество объявлений в смарт-пакете */
  totalAnnouncementsCount: number;
  /** Общее количество бонусов в смарт-пакете */
  totalAuctionPointsCount: number;
  /** Сервисы в смарт-пакете */
  services: IRecommendedServicePackageServiceSchema[];
  /** Тип пакета */
  type: EType;
  /** Полигоны пакета */
  polygonIds: number[];
}

export interface IMultipostingSchema {
  enabled: boolean;
  /** Группа эксперимента **/
  group: string;
}

export interface ICalltrackingSchema {
  /** Список несовместимых сервисов для коллтрекинга */
  incompatibleServices?: Array<EServiceType> | null;
  /** Доступен ли коллтрекинг */
  isAvailable: boolean;
  /** Коллтрекинг обязателен к применению */
  isCompulsory: boolean;
  /** Срок размещения */
  period?: IServicePeriodSchema | null;
  /** Количество подменных номеров колтрекинга */
  calltrackingPhoneSubstitutionsCount: number | null;
}

export interface IPublicationSchema {
  /** Срок публикации объявления */
  period: IServicePeriodSchema;
}

export enum EUnavailableReason {
  /** Заблокировано администрацией */
  deniedBySanctions = 'deniedBySanctions',
  /** Нет фотографий */
  noPhotos = 'noPhotos',
  /** Доступно после окончания периода публикации Премиум */
  onlyAfterPremiumServiceExpiration = 'onlyAfterPremiumServiceExpiration',
  /** Не является участником акции */
  onlyInAction = 'onlyInAction',
  /** Не выполнены условия, необходимые для размещения Топ-объявлений */
  top3ServiceDenied = 'top3ServiceDenied',
}

export enum EPublicationType {
  /** Ограниченная по дням публикация, подневная тарификация */
  dailyLimited = 'dailyLimited',
  /** Бессрочная публикация, подневная тарификация */
  dailyTermless = 'dailyTermless',
  /** Тарификация за весь период */
  periodical = 'periodical',
}

export enum EServiceType {
  /** Бесплатная публикация */
  free = 'free',
  /** Платная публикация */
  paid = 'paid',
  /** Премиум публикация */
  premium = 'premium',
  /** Топ */
  top3 = 'top3',
  /** Колтрекинг */
  calltracking = 'calltracking',
  /** Аукцион */
  auction = 'auction',
}

export enum EType {
  /** Общее предложение (Быстрая сделка, Vip-пакет) */
  'CommonOffer' = 'commonOffer',
  /** Специальное предложение - сгенерировано на основе промокода */
  'SpecialOffer' = 'specialOffer',
  /** Персональное предложение - будет генериться на отдельных условиях */
  'PersonalOffer' = 'personalOffer',
  /** Общее предложение - подписки на публикации */
  'CommonSubscription' = 'commonSubscription',
  /** Смарт-пакет */
  'SmartPackage' = 'smartPackage',
  /** All-in, частный случай смарт-пакета, ключевое отличие: 1 - может пролонгироваться, 2 - может не иметь даты окончания, на фронт отдается через штатную ручку get-smart-package */
  'AllIn' = 'allIn',
  /** Подписка АБ */
  'Subscription' = 'subscription',
}

export interface IRecommendedServicePackageServiceSchema {
  /* Услуга */
  serviceType: EServiceType;
  /** Кол-во объявлений */
  count: number;
  /** Цена после применения скидки */
  price: number;
  /** Баллы СА */
  auctionPoints: number;
  /** Длительность предложения в днях */
  durationInDays: number;
}

export interface IPublishTermsResponseError {
  errors: any[];
  message: string;
}

export type TResponses = IPublishTermsResponse | IPublishTermsResponseError;

export interface IMappers<TResponse200, TResponse400> {
  200(response: IPublishTermsResponse): TResponse200;
  400(response: IPublishTermsResponseError): TResponse400;
}
